import { forwardRef, useImperativeHandle, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SearchInput } from "components/common/SearchInput";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import { GroupsApprovalBarV3 } from "../GroupsApprovalBarV3";
import { PageScaleControl } from "../PageScaleControl";
import { PageScaleInfo } from "../PageScaleInfo";
import { ElementGroupScrollableDivideList } from "../../ReviewPage/ElementGroupsPanel/ElementGroupScrollableDivideList";
import { ElementGroupDefaultRow } from "../../ReviewPage/ElementGroupsPanel/ElementGroupDefaultRow";
import { ApprovalStatusCheckbox } from "../../ReviewPage/ApprovalStatusCheckbox";
import { EmptyStateMessage } from "../../ReviewPage/EmptyStateMessage";
import { RouteInspectorV3 } from "../RouteInspectorV3";
import { AllRoutesGroupRow } from "../AllRoutesGroupRow";
import { AddRouteButton, RoutesCreationModeV3 } from "../RoutesCreationModeV3";
import { SelectionAreasControl } from "../SelectionAreasControl";
import { SelectionAreasMode } from "../SelectionAreasMode";
import { ExportImpactChip } from "../ExportImpactChip";
import { ExportImpactCount } from "../ExportImpactCount";
import { GroupVisibilityToggleButton } from "../GroupVisibilityToggleButton";
import { NeedsReviewIcon } from "../NeedsReviewIcon";
import { TabSortControl } from "../TabSortControl";
import { sortTabList } from "../utils/sortTabList";
import type { CableGroupsTabV3Handlers, CableGroupsTabV3Props } from "./types";

export const CableGroupsTabV3 = forwardRef<
  CableGroupsTabV3Handlers,
  CableGroupsTabV3Props
>(function CableGroupsTabV3(
  {
    canEdit,
    workMode,
    cableSearch,
    onCableSearchChange,
    sort,
    onSortChange,
    routeGroups,
    exportRouteGroups,
    routeGroupLabels,
    byGroupLabel,
    selectedRouteGroupLabel,
    selectedGroupRoutes,
    selectedRoute,
    selectedPlanRecordIndex,
    totalCableMeters,
    allPlanCablesApproved,
    isLoadingApprovals,
    isCableGroupApproved,
    onOpenBulkApproval,
    onToggleCableGroupApproval,
    onSelectRouteGroup,
    onJumpToSelectedRouteGroup,
    onSelectRoute,
    routeGroupOptions,
    onRenameRouteGroup,
    onMarkRouteGroupReviewed,
    onToggleSuspicious,
    onDeleteRoute,
    onDeleteRouteGroup,
    onChangeRouteType,
    isTraversing,
    onStartTraversing,
    onStopTraversing,
    seenIds,
    isAllRoutesMode,
    onSelectAllRoutes,
    isCreationMode,
    onOpenCreationMode,
    isSelectionAreasMode,
    selectionAreasState,
    onOpenAreasPanel,
    areasCount,
    areAreasVisible,
    onToggleAreasVisibility,
    onOpenCalibration,
    isCalibrating,
    onCancelCalibration,
    pageScaleValue,
    pageScaleSource,
    isPageCalibrated,
    isPageRecalibrating,
    isChangePageScaleDisabled,
    onChangePageScale,
    onCancelPageRecalibration,
    creationModeState,
    isPathEditing,
    isPathEditingDirty,
    isPathEditingSubmitting,
    pathEditingMeters,
    onStartPathEditing,
    onSubmitPathEditing,
    onCancelPathEditing,
    hiddenRouteIds,
    onToggleRoutesVisibility,
    areAllRoutesHidden,
    boqPositions,
    onSaveLvMatch,
  }: CableGroupsTabV3Props,
  ref,
) {
  const { t } = useTranslation();

  const { scrollToTarget, containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLLIElement
  >({
    resetDep: `${selectedRouteGroupLabel ?? "all"}-${selectedPlanRecordIndex}-${isSelectionAreasMode}`,
    options: { block: "start" },
  });

  const sortedRouteGroups = useMemo(
    () =>
      sortTabList({
        items: routeGroups,
        mode: sort,
        getLabel: (routeGroup) => routeGroup.label,
        getQuantity: (routeGroup) => routeGroup.meters,
      }),
    [routeGroups, sort],
  );

  const totalRouteCount = useMemo(
    () =>
      routeGroupLabels.reduce(
        (sum, label) => sum + (byGroupLabel[label]?.routes.length ?? 0),
        0,
      ),
    [routeGroupLabels, byGroupLabel],
  );

  // A label absent from the export groups means every route of it was dropped.
  const { exportMetersByLabel, totalExportCableMeters, totalExportRouteCount } =
    useMemo(() => {
      const metersByLabel = new Map<string, number>();
      let totalMeters = 0;
      let totalRoutes = 0;
      for (const routeGroup of exportRouteGroups) {
        metersByLabel.set(routeGroup.label, routeGroup.meters);
        totalMeters += routeGroup.meters;
        totalRoutes += routeGroup.routes.length;
      }
      return {
        exportMetersByLabel: metersByLabel,
        totalExportCableMeters: totalMeters,
        totalExportRouteCount: totalRoutes,
      };
    }, [exportRouteGroups]);

  const allRouteIds = useMemo(
    () => routeGroups.flatMap((group) => group.routes.map((route) => route.id)),
    [routeGroups],
  );

  useImperativeHandle(
    ref,
    () => ({ scrollToSelectedSection: scrollToTarget }),
    [scrollToTarget],
  );

  const isExecutionMode = workMode === "execution";
  const canEditRoutes = canEdit && !isExecutionMode;
  const normalizedCableSearch = cableSearch.trim().toLowerCase();

  if (isSelectionAreasMode) {
    return <SelectionAreasMode state={selectionAreasState} />;
  }

  if (isCreationMode) {
    return (
      <RoutesCreationModeV3
        creationModeState={creationModeState}
        onOpenCalibration={onOpenCalibration}
        isCalibrating={isCalibrating}
        onCancelCalibration={onCancelCalibration}
      />
    );
  }

  if (routeGroups.length === 0) {
    return (
      <EmptyStateMessage
        message={t("smartSurveyor.reviewPage.noCableGroups")}
        action={
          canEditRoutes ? (
            <AddRouteButton onClick={onOpenCreationMode} />
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <SearchInput
          value={cableSearch}
          onChange={onCableSearchChange}
          placeholder={t("smartSurveyor.reviewPage.searchPlaceholder")}
          className="flex-1"
        />
        <TabSortControl value={sort} onChange={onSortChange} />
      </div>
      <GroupsApprovalBarV3
        summaryText={`${t("smartSurveyor.reviewPage.groupsCount", { count: routeGroups.length })} • ${t("smartSurveyor.reviewPage.totalMeters", { meters: Math.round(totalCableMeters) })}`}
        allApproved={allPlanCablesApproved}
        onToggleApproval={onOpenBulkApproval}
        isLoading={isLoadingApprovals}
        canEdit={canEditRoutes}
        summaryAdornment={
          Math.round(totalExportCableMeters) !==
            Math.round(totalCableMeters) && (
            <ExportImpactChip
              label={`${Math.round(totalExportCableMeters)} m`}
              tooltip={t("smartSurveyor.selectionAreas.exportImpactTooltip", {
                exportValue: `${Math.round(totalExportCableMeters)} m`,
                totalValue: `${Math.round(totalCableMeters)} m`,
              })}
            />
          )
        }
        endAdornment={
          <PageScaleInfo scale={pageScaleValue} source={pageScaleSource} />
        }
        startAdornment={
          <>
            {!isExecutionMode && (
              <SelectionAreasControl
                count={areasCount}
                canEdit={canEdit}
                areAreasVisible={areAreasVisible}
                onOpenPanel={onOpenAreasPanel}
                onToggleVisibility={onToggleAreasVisibility}
              />
            )}
            <PageScaleControl
              isCalibrated={isPageCalibrated}
              canEdit={canEditRoutes}
              isPageRecalibrating={isPageRecalibrating}
              isChangePageScaleDisabled={isChangePageScaleDisabled}
              onChangePageScale={onChangePageScale}
              onCancelPageRecalibration={onCancelPageRecalibration}
            />
            {canEditRoutes && <AddRouteButton onClick={onOpenCreationMode} />}
          </>
        }
      />
      <ul className="overflow-hidden flex flex-col min-h-0">
        <ElementGroupScrollableDivideList
          containerRef={containerRef}
          className="p-1"
        >
          <AllRoutesGroupRow
            canEdit={canEditRoutes}
            totalRouteCount={
              totalExportRouteCount !== totalRouteCount ? (
                <ExportImpactCount
                  totalLabel={String(totalRouteCount)}
                  exportLabel={String(totalExportRouteCount)}
                  tooltip={t(
                    "smartSurveyor.selectionAreas.exportImpactTooltip",
                    {
                      exportValue: totalExportRouteCount,
                      totalValue: totalRouteCount,
                    },
                  )}
                />
              ) : (
                totalRouteCount
              )
            }
            isAllRoutesMode={isAllRoutesMode}
            onSelectAllRoutes={onSelectAllRoutes}
            targetRef={targetRef}
            selectedGroupLabel={selectedRouteGroupLabel}
            selectedGroupRoutes={selectedGroupRoutes}
            selectedRoute={selectedRoute}
            routeGroups={routeGroups}
            routeGroupOptions={routeGroupOptions}
            onSelectRoute={onSelectRoute}
            onJumpToSelectedRouteGroup={onJumpToSelectedRouteGroup}
            onRenameGroup={onRenameRouteGroup}
            onDeleteRoute={onDeleteRoute}
            onChangeRouteType={onChangeRouteType}
            isTraversing={isTraversing}
            onStartTraversing={onStartTraversing}
            onStopTraversing={onStopTraversing}
            seenIds={seenIds}
            isPathEditing={isPathEditing}
            isPathEditingDirty={isPathEditingDirty}
            isPathEditingSubmitting={isPathEditingSubmitting}
            pathEditingMeters={pathEditingMeters}
            onStartPathEditing={onStartPathEditing}
            onSubmitPathEditing={onSubmitPathEditing}
            onCancelPathEditing={onCancelPathEditing}
            allRouteIds={allRouteIds}
            hiddenRouteIds={hiddenRouteIds}
            onToggleRoutesVisibility={onToggleRoutesVisibility}
            areAllRoutesHidden={areAllRoutesHidden}
            boqPositions={boqPositions}
            onSaveLvMatch={onSaveLvMatch}
          />
          {sortedRouteGroups
            .filter(
              (routeGroup) =>
                routeGroup.label
                  .toLowerCase()
                  .includes(normalizedCableSearch) ||
                routeGroup.label === selectedRouteGroupLabel,
            )
            .map((routeGroup, index) => {
              const isGroupSelected =
                selectedRouteGroupLabel === routeGroup.label;
              const isGroupHidden = routeGroup.routes.every((route) =>
                hiddenRouteIds.has(route.id),
              );
              const isGroupApproved = isCableGroupApproved(
                routeGroup.route_type_id,
              );
              const isSuspiciousUnapproved =
                !isGroupApproved && routeGroup.cableTypeStatus === "suspicious";
              const exportMeters =
                exportMetersByLabel.get(routeGroup.label) ?? 0;

              return (
                <ElementGroupDefaultRow
                  key={routeGroup.label}
                  index={index}
                  title={routeGroup.label}
                  highlightQuery={normalizedCableSearch}
                  isSelected={isGroupSelected}
                  onClick={() =>
                    !isGroupSelected && onSelectRouteGroup(routeGroup.label)
                  }
                  rowRef={isGroupSelected ? targetRef : undefined}
                  className={
                    isGroupApproved
                      ? "bg-green-50 hover:bg-green-100"
                      : isSuspiciousUnapproved
                        ? "bg-amber-50 hover:bg-amber-100"
                        : undefined
                  }
                  containerClassName={
                    isGroupSelected
                      ? "!border-2 !border-blue-400 rounded-lg overflow-hidden my-1"
                      : undefined
                  }
                  startAdornment={
                    <GroupVisibilityToggleButton
                      isHidden={isGroupHidden}
                      onToggle={() =>
                        onToggleRoutesVisibility(
                          routeGroup.routes.map((route) => route.id),
                        )
                      }
                      title={
                        isGroupHidden
                          ? t("smartSurveyor.reviewPage.showGroup")
                          : t("smartSurveyor.reviewPage.hideGroup")
                      }
                    />
                  }
                  endAdornment={
                    <div className="flex items-center gap-2">
                      {Math.round(exportMeters) !==
                      Math.round(routeGroup.meters) ? (
                        <ExportImpactCount
                          totalLabel={`${Math.round(routeGroup.meters)} m`}
                          exportLabel={`${Math.round(exportMeters)} m`}
                          tooltip={t(
                            "smartSurveyor.selectionAreas.exportImpactTooltip",
                            {
                              exportValue: `${Math.round(exportMeters)} m`,
                              totalValue: `${Math.round(routeGroup.meters)} m`,
                            },
                          )}
                        />
                      ) : (
                        <span className="text-gray-500 flex-shrink-0">{`(${Math.round(routeGroup.meters)} m)`}</span>
                      )}
                      {isSuspiciousUnapproved && !isGroupSelected && (
                        <NeedsReviewIcon />
                      )}
                      {!isExecutionMode && (
                        <ApprovalStatusCheckbox
                          checked={isGroupApproved}
                          onToggle={() =>
                            onToggleCableGroupApproval(routeGroup.route_type_id)
                          }
                        />
                      )}
                    </div>
                  }
                >
                  {isGroupSelected && (
                    <RouteInspectorV3
                      canEdit={canEditRoutes}
                      selectedGroupLabel={routeGroup.label}
                      groupRoutes={selectedGroupRoutes}
                      selectedRoute={selectedRoute}
                      groupLvMatch={routeGroup.routes[0]?.lvMatch}
                      boqPositions={boqPositions}
                      onSaveLvMatch={onSaveLvMatch}
                      groupColor={routeGroup.rgb}
                      routeGroupOptions={routeGroupOptions}
                      onSelectRoute={onSelectRoute}
                      onRenameGroup={onRenameRouteGroup}
                      cableTypeStatus={routeGroup.cableTypeStatus}
                      onMarkReviewed={() =>
                        onMarkRouteGroupReviewed(routeGroup.label)
                      }
                      onToggleSuspicious={onToggleSuspicious}
                      onDeleteRoute={onDeleteRoute}
                      onDeleteRouteGroup={onDeleteRouteGroup}
                      onChangeRouteType={onChangeRouteType}
                      isTraversing={isTraversing}
                      onStartTraversing={onStartTraversing}
                      onStopTraversing={onStopTraversing}
                      seenIds={seenIds}
                      isPathEditing={isPathEditing}
                      isPathEditingDirty={isPathEditingDirty}
                      isPathEditingSubmitting={isPathEditingSubmitting}
                      pathEditingMeters={pathEditingMeters}
                      onStartPathEditing={onStartPathEditing}
                      onSubmitPathEditing={onSubmitPathEditing}
                      onCancelPathEditing={onCancelPathEditing}
                      hiddenRouteIds={hiddenRouteIds}
                      onToggleRoutesVisibility={onToggleRoutesVisibility}
                    />
                  )}
                </ElementGroupDefaultRow>
              );
            })}
        </ElementGroupScrollableDivideList>
      </ul>
    </>
  );
});
